import React, { useEffect, useState } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import Avatar from '../shared/Avatar';
import { LineIcon, WhereIcon } from '../shared/LineIcon';

// The small capitals under each figure in the bottom strip of the panel.
const HERO_LABEL = 'text-[10px] font-semibold uppercase tracking-wider text-slate-300 leading-tight mt-0.5';

const SLOGANS = [
  'Hit every milestone — one delivery at a time.',
  'Your tasks drive the business forward.',
  'Every milestone delivered is a promise kept.',
  'Great teams ship on time.',
  'Milestones don\'t miss themselves.',
  'Deliver with confidence. Every project, every time.',
  'On time. On budget. On point.',
  'Keep the momentum — check your tasks today.',
  'Quality in every deliverable, pride in every deadline.',
  'The work you do today shapes the project you deliver tomorrow.',
];


// ─── Where are you today? ────────────────────────────────────────────────────
//
// One tap on the home page instead of opening Bookings. The buttons are built
// from the live offices plus the non-office statuses, so adding an office makes
// a button appear with no code change.
//
// Nothing resets it overnight: whereabouts are stored per date, so tomorrow
// simply has no entry until somebody sets one. A nightly job to "clear" it
// would be a job that can fail and leave yesterday's answer showing.
function WhereToday({ user, onNavigate }) {
  const [statuses, setStatuses]   = useState([]);
  const [locations, setLocations] = useState([]);
  const [current, setCurrent]     = useState(null);
  const [busy, setBusy]           = useState(false);
  // The desk-required office whose button was clicked, while its warning is up.
  const [warnOffice, setWarnOffice] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const [sts, locs, grid] = await Promise.all([
      api.get('whereabouts/statuses').catch(() => []),
      api.get('desk-locations').catch(() => []),
      api.get('whereabouts', { from: today, to: today }).catch(() => null),
    ]);
    setStatuses((sts || []).filter(x => x.is_selectable));
    setLocations(locs || []);
    setCurrent(grid?.days?.[today]?.[user?.id] || null);
  };
  useEffect(() => { if (user?.id) load(); /* eslint-disable-next-line */ }, [user?.id]);

  const set = async (status_code, location_id, acceptNoDesk = false) => {
    setBusy(true);
    try {
      await api.put('whereabouts', {
        dates: [today], status_code,
        ...(location_id ? { location_id } : {}),
        // Stating an office with desks holds no seat, and the server refuses it
        // unless the person has been warned of that and accepted it.
        ...(acceptNoDesk ? { accept_no_desk: true } : {}),
      });
      await load();
    } catch { /* the panel is a shortcut, not the system of record */ }
    finally { setBusy(false); }
  };

  if (!statuses.length) return null;

  // An office button per office, then everything that is not "in the office".
  // An office with seats (desk_required) cannot be set in one tap: its button
  // opens a warning first, because saying "at PP" here books nothing.
  const options = [
    ...locations.map(l => ({
      key: `office:${l.id}`, label: l.name, icon: null, code: 'office',
      locationId: l.id, deskRequired: l.desk_required !== 0,
    })),
    ...statuses.filter(st => st.code !== 'office')
      .map(st => ({ key: st.code, label: st.label, icon: st.icon, code: st.code, locationId: null })),
  ];

  const isOn = (o) => {
    if (!current) return false;
    if (o.code !== 'office') return current.code === o.code;
    // A desk booking also means "in the office", so match on the office name
    // when the answer came from a booking rather than a stated day.
    return current.code === 'office'
      && (current.detail || '').toLowerCase().includes(String(o.label).toLowerCase());
  };

  return (
    <div className="mb-2.5 px-3 py-2 bg-white/5 rounded-xl border border-white/10 flex flex-wrap items-center gap-x-3 gap-y-2">
      <span className="text-[11px] font-semibold text-slate-300 uppercase tracking-widest">Today I&rsquo;m</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map(o => (
          <button
            key={o.key}
            type="button"
            disabled={busy}
            onClick={() => o.deskRequired ? setWarnOffice(o) : set(o.code, o.locationId)}
            aria-pressed={isOn(o)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full text-[11px] font-medium transition-colors disabled:opacity-50 ${
              isOn(o)
                ? 'bg-[#E5B87A] text-[#0D1D32]'
                : 'bg-white/[0.07] text-slate-200 hover:bg-white/[0.14]'
            }`}
          >
            <WhereIcon code={o.code} fallback={o.icon} />
            {o.label}
          </button>
        ))}
      </div>
      {current && (
        <span className="ml-auto text-[11px] text-slate-300 inline-flex items-center gap-1">
          <WhereIcon code={current.code} fallback={current.icon} className="w-3 h-3" />
          {current.label}{current.detail ? `: ${current.detail}` : ''}
        </span>
      )}

      {/* ── LOCATION IS NOT A SEAT ──
          One tap on PP or RW sets where you are and books nothing, which used to
          be done silently. Say so before it happens, and put what the person
          probably wants (a desk) on the primary button rather than reporting
          afterwards that they have no seat. */}
      {warnOffice && (
        <Modal
          title={`At ${warnOffice.label} without a desk?`}
          onClose={() => setWarnOffice(null)}
          footer={
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => { const o = warnOffice; setWarnOffice(null); set(o.code, o.locationId, true); }}
                className="px-3 py-2 rounded-md border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                I accept
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => { const o = warnOffice; setWarnOffice(null); onNavigate && onNavigate('my-bookings', o.locationId); }}
                className="px-3 py-2 rounded-md border border-navy bg-navy text-xs font-semibold text-white hover:bg-navy/90 disabled:opacity-50"
              >
                Book a desk
              </button>
            </>
          }
        >
          <p className="text-xs text-gray-700 leading-relaxed">
            This only sets your location for today. It does not book you a desk, so we
            cannot guarantee there will be one free for you at {warnOffice.label}.
          </p>
          <p className="text-xs text-gray-700 leading-relaxed mt-2">
            We suggest booking a desk instead: holding a seat keeps it yours and sets
            your location at the same time.
          </p>
        </Modal>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function relativeTime(timestamp) {
  if (!timestamp) return null;
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return null;
  if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(diff / 86400000);
  if (days < 14) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return null;
}

export default function MyDayPanel({ user, onNavigate, businessStats }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [sloganIdx, setSloganIdx] = useState(() => Math.floor(Math.random() * SLOGANS.length));

  useEffect(() => {
    api.get('my-day')
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Company card receipts used to be a red banner here. They are a tile in the
  // My Space row below (MySpaceStrip), beside holiday and time off, which also
  // says "all in" when there is nothing to do rather than saying nothing.

  useEffect(() => {
    const id = setInterval(() => setSloganIdx(i => (i + 1) % SLOGANS.length), 9000);
    return () => clearInterval(id);
  }, []);

  // "+N more out" named nobody: you could see three colleagues were away and
  // had to open the leave calendar to find out which three. Hovering it now
  // lists them. State rather than a title attribute, because the list carries
  // an avatar and two lines per person, and it opens on focus too so it is not
  // hover-only.
  const [showAllOut, setShowAllOut] = useState(false);

  if (loading) {
    return <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-4 mb-6 text-white animate-pulse min-h-[80px]" />;
  }

  if (!data) return null;

  const {
    hours_today = 0,
    hours_week = 0,
    tasks_due = [],
    team_out = [],
    // ── LEAVE ONLY, AND IT LEADS WHERE THE THING IS ───────────────────
    //
    // "Quite often I get pending approvals, but when clicked, there is nothing to
    // approve."
    //
    // The count used to be leave AND overtime while the button went to Holidays
    // either way, so a pending overtime claim read as "1 Pending approvals" and led
    // to a leave screen saying "No pending requests": the number was right and the
    // destination was wrong. Splitting it gave each kind its own chip.
    //
    // Overtime has since left the chip altogether. A claim is an Action, and the My
    // Actions panel below carries the Approve and Reject themselves, so a number
    // counting claims and pointing at a third screen is noise beside the decision.
    // pending_ot is still returned by the endpoint and deliberately not read here.
    // Leave has no Action of its own, so it keeps its chip.
    pending_leave = 0,
    missed_milestones_pending = [],
  } = data;

  const approvalChips = pending_leave
    ? [{ key: 'leave', count: pending_leave, page: 'approvals', tab: 'leave', label: pending_leave === 1 ? 'Leave request' : 'Leave requests' }]
    : [];

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const today = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const lastSeen = relativeTime(user?.last_login);

  return (
    <div className="hero bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl px-5 py-3.5 mb-6 text-white shadow-lg">

      {/* ── Greeting row ── */}
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <div className="flex items-baseline gap-x-2 flex-wrap">
            <span className="text-base font-bold leading-tight">{greeting}, {user?.first_name || 'there'}</span>
            <span className="text-[12px] text-slate-300">{today}</span>
          </div>
          {lastSeen && (
            <div className="text-[11px] text-slate-300 mt-0.5">Last here {lastSeen}</div>
          )}
        </div>
        {approvalChips.length > 0 && (
          <div className="flex-shrink-0 flex items-center gap-1.5 flex-wrap justify-end">
            {approvalChips.map(chip => (
              <button
                key={chip.key}
                onClick={() => onNavigate && onNavigate(chip.page, chip.tab)}
                title="Waiting for you to approve. Opens Approvals."
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E5B87A] hover:bg-[#d9a865] text-[#0D1D32] text-xs font-bold rounded-full transition-colors"
              >
                <span className="min-w-[18px] h-[18px] rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-[11px] px-1 leading-none">
                  {chip.count > 99 ? '99+' : chip.count}
                </span>
                {chip.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Slogan, under the date ── */}
      <div key={sloganIdx} className="mt-0.5 mb-2.5 text-[12px] font-medium text-[#E5B87A] leading-snug transition-opacity duration-700">
        {SLOGANS[sloganIdx]}
      </div>

      <WhereToday user={user} onNavigate={onNavigate} />

      {/* ── Missed milestones warning — action required ── */}
      {missed_milestones_pending.length > 0 && (
        <div className="mb-2.5 px-3 py-2 bg-[#7f1d1d]/40 border border-[#ef4444]/40 rounded-xl">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="inline-flex items-center gap-1.5 text-red-200 font-bold text-xs">
              <LineIcon name="alert" />
              Action required: {missed_milestones_pending.length > 1 ? 'missed milestones need' : 'a missed milestone needs'} a reason
            </span>
            <span className="min-w-[18px] h-[18px] rounded-full bg-red-500 text-white flex items-center justify-center font-bold text-[11px] px-1 leading-none">
              {missed_milestones_pending.length > 99 ? '99+' : missed_milestones_pending.length}
            </span>
          </div>
          <div className="space-y-0.5">
            {missed_milestones_pending.slice(0, 3).map(m => (
              <div
                key={m.id}
                className="text-[12px] text-slate-100 truncate cursor-pointer hover:text-white hover:underline"
                onClick={() => onNavigate && onNavigate('project-update', m.sow_id)}
                title="Open this project in Project Update"
              >
                <span className="font-semibold">{m.sow_reference} · {m.milestone_code}</span>{m.description ? `: ${m.description}` : ''}{' '}
                <span className="text-red-200">(due {fmtDate(m.forecast_date)})</span>
              </div>
            ))}
            {missed_milestones_pending.length > 3 && (
              <div
                className="text-[12px] text-red-200 cursor-pointer hover:text-white hover:underline"
                onClick={() => onNavigate && onNavigate('project-update')}
              >+{missed_milestones_pending.length - 3} more: open Project Update</div>
            )}
          </div>
        </div>
      )}

      {/* What is waiting on this person is listed by the My Actions panel
          below the greeting, not here. This carried a compact copy: a count
          and the first three titles, with no way to act on any of them. The
          panel states all of them and decides an overtime claim in place, so
          a second heading saying the same thing less usefully has gone. */}

      {/* Hours, tasks and team out share one row with the SOW totals. */}
      <div className="border-t border-white/10 pt-2.5 flex flex-wrap items-end gap-x-8 gap-y-2.5">
      {/* ── Compact info bar: hours + tasks + team out ── */}
      {/* Each figure is set like the SOW totals beside it: the value, then
          its label underneath, so the strip reads as one row of figures. */}
      <div className="flex flex-wrap items-end gap-x-7 gap-y-2 text-[12px] text-slate-300">
        <span className="flex flex-col">
          <span className="text-base font-extrabold text-white leading-tight">{hours_today.toFixed(1)}h</span>
          <span className={HERO_LABEL}>Today</span>
        </span>
        <span className="flex flex-col">
          <span className="text-base font-extrabold text-white leading-tight">{hours_week.toFixed(1)}h</span>
          <span className={HERO_LABEL}>This week</span>
        </span>
        <span
          className="flex flex-col cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => onNavigate && onNavigate('my-tasks')}
        >
          {tasks_due.length === 0
            ? <span className="text-base font-extrabold text-green-400 leading-tight">All clear</span>
            : <span className="text-base font-extrabold text-white leading-tight">{tasks_due.length} due</span>}
          <span className={HERO_LABEL}>Tasks this week</span>
        </span>
        {team_out.length > 0 && (
        <span className="flex flex-col">
        <span className="flex items-baseline gap-x-3 gap-y-1 flex-wrap leading-tight">
        {team_out.slice(0, 4).map((p, i) => (
          <span key={i} className="flex items-baseline gap-1" title={`${p.person_name}, ${p.leave_type} leave`}>
            <Avatar as="span" personId={p.person_id}
              className="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
              style={{ backgroundColor: p.avatar_color || '#4B5563' }}
            >
              {(p.person_name || '?')[0]}
            </Avatar>
            <span className="text-base font-extrabold text-white leading-tight">{p.person_name?.split(' ')[0]}</span>
          </span>
        ))}
        {team_out.length > 4 && (
          <span
            className="relative cursor-default text-slate-300 underline decoration-dotted underline-offset-2 hover:text-slate-300 focus:text-slate-300 focus:outline-none transition-colors"
            tabIndex={0}
            onMouseEnter={() => setShowAllOut(true)}
            onMouseLeave={() => setShowAllOut(false)}
            onFocus={() => setShowAllOut(true)}
            onBlur={() => setShowAllOut(false)}
          >
            +{team_out.length - 4} more
            {/* Right-anchored: the chip is the last thing on the bar, so a list
                growing leftwards stays on screen where one growing rightwards
                runs off the edge of the panel. */}
            {showAllOut && (
              <div className="absolute bottom-full right-0 mb-2 z-30 w-56 rounded-lg border border-white/15 bg-slate-900 p-2.5 shadow-xl">
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Also out
                </div>
                <div className="space-y-1.5">
                  {team_out.slice(4).map((p, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <Avatar as="span" personId={p.person_id}
                        className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                        style={{ backgroundColor: p.avatar_color || '#4B5563' }}
                      >
                        {(p.person_name || '?')[0]}
                      </Avatar>
                      <span className="min-w-0">
                        <span className="block truncate text-[12px] text-white">{p.person_name}</span>
                        <span className="block text-[11px] text-slate-400">
                          {p.leave_type} · {fmtDate(p.start_date)}
                          {p.end_date && p.end_date !== p.start_date ? ` to ${fmtDate(p.end_date)}` : ''}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </span>
        )}
        </span>
        <span className={HERO_LABEL}>Out today</span>
        </span>
        )}
      </div>

      {/* ── Business overview stats ── */}
      {/* A rule between the two groups keeps the strip one row of figures. */}
      {businessStats && businessStats.length > 0 && (
        <span aria-hidden="true" className="hidden lg:block w-px h-9 bg-white/15" />
      )}
      {businessStats && businessStats.length > 0 && (
        <div className="flex flex-wrap gap-x-7 gap-y-2">
          {businessStats.map(s => (
            <div key={s.label} className="text-right min-w-0">
              <div className={`text-base font-extrabold truncate ${s.accent || 'text-[#E5B87A]'}`}>{s.value}</div>
              <div className={HERO_LABEL}>{s.label}</div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
